/**
 * Whitelist Account-IDs voor de B2B Inkooporder-wizard.
 *
 * Server- en clientside identiek (één Salesforce org, geen sandbox-deps in deze
 * flow). Account-IDs zijn niet gevoelig dus ingebakken in code; nieuwe
 * wederverkoper toevoegen = één regel + deploy.
 *
 * De Apex-service `OfficeB2BInkooporderService` heeft dezelfde whitelist
 * ingebakken — frontend-check is defense-in-depth.
 */

export interface B2BAccount {
  name: string;
}

export const B2B_INKOOPORDER_ACCOUNTS: Record<string, B2BAccount> = {
  '0019Y0000052IxPQAU': { name: 'Partou' },
  '0019Y000005LaJXQA0': { name: 'Smallsteps' },
};

export function isB2BInkooporderAccount(accountId: string | null | undefined): boolean {
  if (!accountId) return false;
  return accountId in B2B_INKOOPORDER_ACCOUNTS;
}

export function getB2BAccountName(accountId: string): string | null {
  return B2B_INKOOPORDER_ACCOUNTS[accountId]?.name ?? null;
}

// =========================================================================
// AtlasKidtech — vaste-prijs B2B-inkooporder via PDF-upload
// =========================================================================

export const ATLAS_KIDTECH_ACCOUNT_ID = '001Oj00000F5YelIAF';
export const ATLAS_KIDTECH_ACCOUNT_NAME = 'AtlasKidtech';

/**
 * Whitelist van canonical AK-productcodes (SF ProductCode → display name).
 * Wordt gebruikt in de preview-tabel om onbekende codes rood te markeren.
 * Spiegel van de lijst in `parse-atlas-pdf.ts` (server-side AI prompt).
 */
export const ATLAS_PRODUCT_CODES: Record<string, string> = {
  'AK - CINREIKUSS': 'Reinigen kussenset',
  'AK - CINREIKUSS-1': 'Reinigen rugkussen',
  'AK - CINREIKUSS-2': 'Reinigen bekkenkussen',
  'AK - CINREIKUSS-3': 'Reinigen zitkussen',
  'AK - CINREIKUSS-4': 'Reinigen hoofdsteun',
  'AK - CINIMPKUSS': 'Impregneren kussenset',
  'AK - CINIMPKUSS-1': 'Impregneren rugkussen',
  'AK - CINIMPKUSS-2': 'Impregneren bekkenkussen',
  'AK - CINIMPKUSS-3': 'Impregneren zitkussen',
  'AK - CINIMPKUSS-4': 'Impregneren hoofdsteun',
  'AK - CINMATGRT': 'Reinigen matras groot (vanaf 90×200)',
  'AK - CINMATKL': 'Reinigen matras klein (tot 90×200)',
  'AK - Tendoek': 'Tendoek reinigen',
  'AK - Vestjes': 'Vestjes reinigen en impregneren',
  'AK - Zitbroeken': 'Zitbroeken reinigen en impregneren',
};

export function isAtlasInkooporderAccount(accountId: string | null | undefined): boolean {
  return !!accountId && accountId === ATLAS_KIDTECH_ACCOUNT_ID;
}

/**
 * B2B-cyclusklanten (klasse "zelf-brengen": brengen, later ophalen, per order
 * factureren via /api/orders/b2b-cyclus-afronden). Spiegel van de server-side
 * whitelist in api/_lib/b2b/cyclus-accounts.ts — daar wijzigen = hier ook.
 * Los van B2B_INKOOPORDER_ACCOUNTS: Smallsteps/Partou factureren mogelijk als
 * verzamelfactuur en horen (nog) niet in deze klasse.
 */
export const B2B_CYCLUS_ACCOUNTS: Record<string, B2BAccount> = {
  [ATLAS_KIDTECH_ACCOUNT_ID]: { name: ATLAS_KIDTECH_ACCOUNT_NAME },
};

export function isB2BCyclusAccount(accountId: string | null | undefined): boolean {
  if (!accountId) return false;
  return accountId in B2B_CYCLUS_ACCOUNTS;
}

/**
 * Geeft de canonical productcode terug (met 'AK - ' prefix) of null als de
 * code niet in de whitelist staat. Accepteert codes met of zonder prefix.
 */
export function resolveAtlasProductCode(raw: string): string | null {
  if (!raw) return null;
  const prefixed = raw.startsWith('AK - ') ? raw : `AK - ${raw}`;
  return prefixed in ATLAS_PRODUCT_CODES ? prefixed : null;
}
